import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency } from '../../lib/i18n';
import {
  DollarSign, TrendingUp, TrendingDown, PlusCircle, Trash2, Edit2, Save, X,
  Receipt, PieChart, ArrowRightLeft, Users, Calendar, Tag, FileText,
  ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Wallet, Handshake,
  History, CreditCard
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPENSE_CATEGORIES = [
  'Technology', 'Marketing', 'Hosting & Domain', 'Design',
  'Legal & Compliance', 'Office & Admin', 'Freelancer / Contractor',
  'Travel', 'Subscription / Tools', 'Miscellaneous'
];

const EARNING_CATEGORIES = [
  'Subscription Revenue', 'Setup Fee', 'Custom Development',
  'Consulting', 'Training', 'Referral Commission', 'Other'
];

const PARTNERS = [
  { key: 'angshuman', name: 'Angshuman Biswas', short: 'Angshuman', share: 65 },
  { key: 'mehedi', name: 'Mehedi Hassan', short: 'Mehedi', share: 35 },
];

const partnerName = (key) => PARTNERS.find(p => p.key === key)?.short || key;

const CATEGORY_COLORS = {
  'Technology': 'bg-blue-100 text-blue-800',
  'Marketing': 'bg-pink-100 text-pink-800',
  'Hosting & Domain': 'bg-cyan-100 text-cyan-800',
  'Design': 'bg-purple-100 text-purple-800',
  'Legal & Compliance': 'bg-yellow-100 text-yellow-800',
  'Office & Admin': 'bg-slate-100 text-slate-800',
  'Freelancer / Contractor': 'bg-orange-100 text-orange-800',
  'Travel': 'bg-green-100 text-green-800',
  'Subscription / Tools': 'bg-indigo-100 text-indigo-800',
  'Miscellaneous': 'bg-gray-100 text-gray-800',
  'Subscription Revenue': 'bg-emerald-100 text-emerald-800',
  'Setup Fee': 'bg-teal-100 text-teal-800',
  'Custom Development': 'bg-violet-100 text-violet-800',
  'Consulting': 'bg-amber-100 text-amber-800',
  'Training': 'bg-lime-100 text-lime-800',
  'Referral Commission': 'bg-rose-100 text-rose-800',
  'Other': 'bg-neutral-100 text-neutral-800',
};

// ─────────────────────────────────────────────────────────
// EXPENSE FORM  (supports "Both" as payer with custom amounts)
// ─────────────────────────────────────────────────────────
function ExpenseForm({ onSave, onCancel, initialData }) {
  const [form, setForm] = useState({
    title: '', description: '', amount: '', category: 'Technology',
    date: new Date().toISOString().slice(0, 10), paid_by: 'angshuman',
    angshuman_paid: '', mehedi_paid: '',
    split_type: 'percentage', angshuman_amount: '', mehedi_amount: '',
    notes: '',
    ...initialData,
    amount: initialData?.amount?.toString() || '',
    angshuman_paid: initialData?.angshuman_paid?.toString() || '',
    mehedi_paid: initialData?.mehedi_paid?.toString() || '',
    angshuman_amount: initialData?.angshuman_amount?.toString() || '',
    mehedi_amount: initialData?.mehedi_amount?.toString() || '',
  });
  const [saving, setSaving] = useState(false);

  const amt = parseFloat(form.amount) || 0;

  // Preview: who should bear (responsibility split — always 65/35 by default)
  const previewSplit = useMemo(() => {
    if (form.split_type === 'amount') {
      const a = parseFloat(form.angshuman_amount) || 0;
      const m = parseFloat(form.mehedi_amount) || 0;
      return { angshuman: a, mehedi: m, valid: Math.abs(a + m - amt) < 0.01 || amt === 0 };
    }
    return { angshuman: amt * 0.65, mehedi: amt * 0.35, valid: true };
  }, [amt, form.split_type, form.angshuman_amount, form.mehedi_amount]);

  // Preview: who actually paid
  const previewPaid = useMemo(() => {
    if (form.paid_by === 'both') {
      const a = parseFloat(form.angshuman_paid) || 0;
      const m = parseFloat(form.mehedi_paid) || 0;
      return { angshuman: a, mehedi: m, valid: Math.abs(a + m - amt) < 0.01 || amt === 0 };
    }
    if (form.paid_by === 'angshuman') return { angshuman: amt, mehedi: 0, valid: true };
    return { angshuman: 0, mehedi: amt, valid: true };
  }, [amt, form.paid_by, form.angshuman_paid, form.mehedi_paid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !amt) return;
    if (form.split_type === 'amount' && !previewSplit.valid) return;
    if (form.paid_by === 'both' && !previewPaid.valid) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        amount: amt,
        angshuman_paid: form.paid_by === 'both' ? parseFloat(form.angshuman_paid) || 0 : null,
        mehedi_paid: form.paid_by === 'both' ? parseFloat(form.mehedi_paid) || 0 : null,
        angshuman_amount: form.split_type === 'amount' ? parseFloat(form.angshuman_amount) || 0 : null,
        mehedi_amount: form.split_type === 'amount' ? parseFloat(form.mehedi_amount) || 0 : null,
      });
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e?.target ? e.target.value : e }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-5 bg-white rounded-xl border-2 border-dashed border-emerald-300 shadow-sm">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <Receipt className="w-5 h-5 text-emerald-600" />
        {initialData ? 'Edit Expense' : 'Add New Expense'}
      </h3>

      {/* Row 1: Title + Amount + Date */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Title *</label>
          <Input value={form.title} onChange={set('title')} placeholder="e.g. Domain renewal" required />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Amount (BDT) *</label>
          <Input type="number" step="0.01" min="0" value={form.amount} onChange={set('amount')} placeholder="0.00" required />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Date *</label>
          <Input type="date" value={form.date} onChange={set('date')} required />
        </div>
      </div>

      {/* Row 2: Category */}
      <div>
        <label className="text-xs font-medium text-slate-500 mb-1 block">Category</label>
        <Select value={form.category} onValueChange={set('category')}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Row 3: Who Paid? */}
      <div>
        <label className="text-xs font-medium text-slate-500 mb-1 block">Who Paid?</label>
        <div className="flex flex-wrap gap-3">
          {[{ key: 'angshuman', label: 'Angshuman Only', color: 'blue' },
            { key: 'mehedi', label: 'Mehedi Only', color: 'emerald' },
            { key: 'both', label: 'Both (Custom Amounts)', color: 'amber' }
          ].map(opt => (
            <label key={opt.key}
              className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-all ${
                form.paid_by === opt.key
                  ? (opt.key === 'angshuman' ? 'border-blue-500 bg-blue-50' : opt.key === 'mehedi' ? 'border-emerald-500 bg-emerald-50' : 'border-amber-500 bg-amber-50')
                  : 'border-slate-200'}`}
            >
              <input type="radio" name="paid_by" checked={form.paid_by === opt.key}
                onChange={() => setForm(f => ({ ...f, paid_by: opt.key }))} className="sr-only" />
              <CreditCard className="w-4 h-4" />
              <span className="text-sm font-medium">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Both paid: custom payment amounts */}
      {form.paid_by === 'both' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div>
            <label className="text-xs font-medium text-amber-700 mb-1 block">Angshuman Paid (BDT)</label>
            <Input type="number" step="0.01" min="0" value={form.angshuman_paid} onChange={set('angshuman_paid')} placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs font-medium text-amber-700 mb-1 block">Mehedi Paid (BDT)</label>
            <Input type="number" step="0.01" min="0" value={form.mehedi_paid} onChange={set('mehedi_paid')} placeholder="0.00" />
          </div>
          {amt > 0 && !previewPaid.valid && (
            <div className="col-span-2 text-red-600 text-xs flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Sum of payments ({formatCurrency((parseFloat(form.angshuman_paid) || 0) + (parseFloat(form.mehedi_paid) || 0))}) doesn't match total ({formatCurrency(amt)})
            </div>
          )}
          {amt > 0 && previewPaid.valid && (
            <div className="col-span-2 text-emerald-600 text-xs flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Payment split: Angshuman {formatCurrency(previewPaid.angshuman)} + Mehedi {formatCurrency(previewPaid.mehedi)} = {formatCurrency(amt)}
            </div>
          )}
        </div>
      )}

      {/* Row 4: Responsibility Split Type */}
      <div>
        <label className="text-xs font-medium text-slate-500 mb-1 block">Responsibility Split (who should bear the cost)</label>
        <div className="flex gap-3">
          <label className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-all ${form.split_type === 'percentage' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}>
            <input type="radio" name="split" checked={form.split_type === 'percentage'} onChange={() => setForm(f => ({ ...f, split_type: 'percentage' }))} className="sr-only" />
            <PieChart className="w-4 h-4" />
            <span className="text-sm font-medium">Default (65% / 35%)</span>
          </label>
          <label className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-all ${form.split_type === 'amount' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}>
            <input type="radio" name="split" checked={form.split_type === 'amount'} onChange={() => setForm(f => ({ ...f, split_type: 'amount' }))} className="sr-only" />
            <DollarSign className="w-4 h-4" />
            <span className="text-sm font-medium">Custom Responsibility</span>
          </label>
        </div>
      </div>

      {/* Custom responsibility split amounts */}
      {form.split_type === 'amount' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-violet-50 rounded-lg border border-violet-200">
          <div>
            <label className="text-xs font-medium text-violet-700 mb-1 block">Angshuman Should Bear (BDT)</label>
            <Input type="number" step="0.01" min="0" value={form.angshuman_amount} onChange={set('angshuman_amount')} placeholder="0.00" />
          </div>
          <div>
            <label className="text-xs font-medium text-violet-700 mb-1 block">Mehedi Should Bear (BDT)</label>
            <Input type="number" step="0.01" min="0" value={form.mehedi_amount} onChange={set('mehedi_amount')} placeholder="0.00" />
          </div>
          {amt > 0 && !previewSplit.valid && (
            <div className="col-span-2 text-red-600 text-xs flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Sum of responsibilities ({formatCurrency((parseFloat(form.angshuman_amount) || 0) + (parseFloat(form.mehedi_amount) || 0))}) doesn't match total ({formatCurrency(amt)})
            </div>
          )}
        </div>
      )}

      {/* Split Preview — shows BOTH who paid and who should bear */}
      {amt > 0 && (
        <div className="p-4 bg-slate-50 rounded-lg border space-y-3">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Expense Breakdown</p>

          {/* Who paid */}
          <div>
            <p className="text-xs text-slate-500 mb-1">Who actually paid:</p>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>Angshuman paid</span>
                  <span className="font-bold text-blue-700">{formatCurrency(previewPaid.angshuman)}</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${amt ? (previewPaid.angshuman / amt * 100) : 0}%` }} />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>Mehedi paid</span>
                  <span className="font-bold text-emerald-700">{formatCurrency(previewPaid.mehedi)}</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${amt ? (previewPaid.mehedi / amt * 100) : 0}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Who should bear */}
          <div>
            <p className="text-xs text-slate-500 mb-1">Who should bear (responsibility):</p>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>Angshuman ({form.split_type === 'percentage' ? '65%' : 'custom'})</span>
                  <span className="font-bold">{formatCurrency(previewSplit.angshuman)}</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-400 rounded-full transition-all" style={{ width: `${amt ? (previewSplit.angshuman / amt * 100) : 0}%` }} />
                </div>
              </div>
              <div className="flex-1">
                <div className="flex justify-between text-sm mb-1">
                  <span>Mehedi ({form.split_type === 'percentage' ? '35%' : 'custom'})</span>
                  <span className="font-bold">{formatCurrency(previewSplit.mehedi)}</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-400 rounded-full transition-all" style={{ width: `${amt ? (previewSplit.mehedi / amt * 100) : 0}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Difference (who owes for this specific expense) */}
          {form.paid_by === 'both' && previewPaid.valid && (
            <div className="pt-2 border-t">
              <p className="text-xs text-slate-500 mb-1">Per-expense difference (Paid - Should Bear):</p>
              <div className="flex gap-4 text-sm">
                <span className={`font-medium ${previewPaid.angshuman - previewSplit.angshuman >= 0 ? 'text-blue-600' : 'text-red-500'}`}>
                  Angshuman: {previewPaid.angshuman - previewSplit.angshuman >= 0 ? '+' : ''}{formatCurrency(previewPaid.angshuman - previewSplit.angshuman)}
                </span>
                <span className={`font-medium ${previewPaid.mehedi - previewSplit.mehedi >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  Mehedi: {previewPaid.mehedi - previewSplit.mehedi >= 0 ? '+' : ''}{formatCurrency(previewPaid.mehedi - previewSplit.mehedi)}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Description + Notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Description</label>
          <Input value={form.description} onChange={set('description')} placeholder="Optional details" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Notes</label>
          <Input value={form.notes} onChange={set('notes')} placeholder="Any additional notes" />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={saving || !form.title || !amt || (form.split_type === 'amount' && !previewSplit.valid) || (form.paid_by === 'both' && !previewPaid.valid)} className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Spinner size="sm" /> : <Save className="w-4 h-4 mr-1" />}
          {initialData ? 'Update Expense' : 'Save Expense'}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        )}
      </div>
    </form>
  );
}


// ─────────────────────────────────────────────────────────
// EARNING FORM
// ─────────────────────────────────────────────────────────
function EarningForm({ onSave, onCancel, initialData }) {
  const [form, setForm] = useState({
    title: '', description: '', amount: '', category: 'Subscription Revenue',
    date: new Date().toISOString().slice(0, 10), source: '', notes: '',
    ...initialData,
    amount: initialData?.amount?.toString() || '',
  });
  const [saving, setSaving] = useState(false);
  const amt = parseFloat(form.amount) || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !amt) return;
    setSaving(true);
    try {
      await onSave({ ...form, amount: amt });
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e?.target ? e.target.value : e }));

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-5 bg-white rounded-xl border-2 border-dashed border-blue-300 shadow-sm">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-blue-600" />
        {initialData ? 'Edit Earning' : 'Add New Earning'}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Title *</label>
          <Input value={form.title} onChange={set('title')} placeholder="e.g. Monthly subscription" required />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Amount (BDT) *</label>
          <Input type="number" step="0.01" min="0" value={form.amount} onChange={set('amount')} placeholder="0.00" required />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Date *</label>
          <Input type="date" value={form.date} onChange={set('date')} required />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Category</label>
          <Select value={form.category} onValueChange={set('category')}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {EARNING_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Source / Client</label>
          <Input value={form.source} onChange={set('source')} placeholder="e.g. Sarker Motors" />
        </div>
      </div>

      {amt > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs font-medium text-blue-600 mb-2">Earnings Split (65% / 35%)</p>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>Angshuman (65%)</span>
                <span className="font-bold">{formatCurrency(amt * 0.65)}</span>
              </div>
              <div className="h-2 bg-blue-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: '65%' }} />
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1">
                <span>Mehedi (35%)</span>
                <span className="font-bold">{formatCurrency(amt * 0.35)}</span>
              </div>
              <div className="h-2 bg-emerald-200 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full" style={{ width: '100%' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Description</label>
          <Input value={form.description} onChange={set('description')} placeholder="Optional details" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-500 mb-1 block">Notes</label>
          <Input value={form.notes} onChange={set('notes')} placeholder="Any additional notes" />
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={saving || !form.title || !amt} className="bg-blue-600 hover:bg-blue-700">
          {saving ? <Spinner size="sm" /> : <Save className="w-4 h-4 mr-1" />}
          {initialData ? 'Update Earning' : 'Save Earning'}
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        )}
      </div>
    </form>
  );
}


// ─────────────────────────────────────────────────────────
// Helper: get actual paid amounts from expense
// ─────────────────────────────────────────────────────────
function getActualPaid(exp) {
  if (exp.actual_paid) return exp.actual_paid;
  // Legacy fallback
  if (exp.paid_by === 'angshuman') return { angshuman: exp.amount, mehedi: 0 };
  if (exp.paid_by === 'mehedi') return { angshuman: 0, mehedi: exp.amount };
  if (exp.paid_by === 'both') return { angshuman: exp.angshuman_paid || 0, mehedi: exp.mehedi_paid || 0 };
  return { angshuman: exp.amount, mehedi: 0 };
}


// ─────────────────────────────────────────────────────────
// EXPENSES TAB
// ─────────────────────────────────────────────────────────
function ExpensesTab({ expenses, settlements, onRefresh, language }) {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCat, setFilterCat] = useState('all');
  const [filterPaidBy, setFilterPaidBy] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);
  const [showSettleForm, setShowSettleForm] = useState(false);
  const [settleData, setSettleData] = useState({ amount: '', notes: '', date: new Date().toISOString().slice(0, 10) });
  const [settling, setSettling] = useState(false);

  const filtered = useMemo(() => {
    let list = [...expenses];
    if (filterCat !== 'all') list = list.filter(e => e.category === filterCat);
    if (filterPaidBy !== 'all') {
      list = list.filter(e => {
        if (filterPaidBy === 'both') return e.paid_by === 'both';
        const ap = getActualPaid(e);
        return ap[filterPaidBy] > 0;
      });
    }
    list.sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (sortDir === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    });
    return list;
  }, [expenses, filterCat, filterPaidBy, sortField, sortDir]);

  // Totals for filtered view
  const totals = useMemo(() => {
    const total = filtered.reduce((s, e) => s + e.amount, 0);
    let angPaid = 0, mehPaid = 0;
    filtered.forEach(e => {
      const ap = getActualPaid(e);
      angPaid += ap.angshuman || 0;
      mehPaid += ap.mehedi || 0;
    });
    const angShare = filtered.reduce((s, e) => s + (e.split?.angshuman || e.amount * 0.65), 0);
    const mehShare = filtered.reduce((s, e) => s + (e.split?.mehedi || e.amount * 0.35), 0);

    // Factor in settlements
    const angSettled = settlements.filter(s => s.paid_by === 'angshuman').reduce((s, st) => s + st.amount, 0);
    const mehSettled = settlements.filter(s => s.paid_by === 'mehedi').reduce((s, st) => s + st.amount, 0);
    const angReceived = settlements.filter(s => s.paid_to === 'angshuman').reduce((s, st) => s + st.amount, 0);
    const mehReceived = settlements.filter(s => s.paid_to === 'mehedi').reduce((s, st) => s + st.amount, 0);

    const angEffective = angPaid + angSettled - angReceived;
    const mehEffective = mehPaid + mehSettled - mehReceived;

    return { total, angPaid, mehPaid, angShare, mehShare, angEffective, mehEffective, angSettled, mehSettled, angReceived, mehReceived };
  }, [filtered, settlements]);

  // Who owes whom?
  const angBalance = totals.angEffective - totals.angShare;
  const settlementNeeded = Math.abs(angBalance) >= 1;
  const owes = angBalance > 0 ? 'Mehedi' : 'Angshuman';
  const owedTo = angBalance > 0 ? 'Angshuman' : 'Mehedi';
  const owesPerson = angBalance > 0 ? 'mehedi' : 'angshuman';
  const owedToPerson = angBalance > 0 ? 'angshuman' : 'mehedi';

  const handleCreate = async (data) => {
    await axios.post(`${API}/saas-expenses`, data);
    toast({ title: 'Expense added!' });
    setShowForm(false);
    onRefresh();
  };

  const handleUpdate = async (data) => {
    await axios.put(`${API}/saas-expenses/${editingId}`, data);
    toast({ title: 'Expense updated!' });
    setEditingId(null);
    onRefresh();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await axios.delete(`${API}/saas-expenses/${id}`);
    toast({ title: 'Expense deleted' });
    onRefresh();
  };

  const handleSettle = async () => {
    const amt = parseFloat(settleData.amount) || 0;
    if (!amt) return;
    setSettling(true);
    try {
      await axios.post(`${API}/saas-settlements`, {
        amount: amt,
        paid_by: owesPerson,
        paid_to: owedToPerson,
        date: settleData.date,
        notes: settleData.notes || `Settlement: ${owes} paid ${owedTo}`,
      });
      toast({ title: 'Settlement recorded!' });
      setShowSettleForm(false);
      setSettleData({ amount: '', notes: '', date: new Date().toISOString().slice(0, 10) });
      onRefresh();
    } catch (err) {
      toast({ title: 'Failed to record settlement', variant: 'destructive' });
    } finally {
      setSettling(false);
    }
  };

  const handleDeleteSettlement = async (id) => {
    if (!window.confirm('Delete this settlement record?')) return;
    await axios.delete(`${API}/saas-settlements/${id}`);
    toast({ title: 'Settlement deleted' });
    onRefresh();
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <p className="text-xs text-red-600 font-medium">Total Expenses</p>
            <p className="text-2xl font-bold text-red-700">{formatCurrency(totals.total, language)}</p>
            <p className="text-xs text-red-500">{filtered.length} entries</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs text-blue-600 font-medium">Angshuman Paid</p>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(totals.angPaid, language)}</p>
            <p className="text-xs text-blue-500">Should bear: {formatCurrency(totals.angShare, language)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-600 font-medium">Mehedi Paid</p>
            <p className="text-xl font-bold text-emerald-700">{formatCurrency(totals.mehPaid, language)}</p>
            <p className="text-xs text-emerald-500">Should bear: {formatCurrency(totals.mehShare, language)}</p>
          </CardContent>
        </Card>
        <Card className={`border-2 ${angBalance > 0 ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 border-slate-200'}`}>
          <CardContent className="p-4">
            <p className="text-xs text-slate-600 font-medium">Angshuman Balance</p>
            <p className={`text-xl font-bold ${angBalance > 0 ? 'text-blue-700' : 'text-red-600'}`}>
              {angBalance > 0 ? '+' : ''}{formatCurrency(angBalance, language)}
            </p>
            <p className="text-xs text-slate-500">{angBalance > 0 ? 'Overpaid' : 'Underpaid'} (incl. settlements)</p>
          </CardContent>
        </Card>
        <Card className={`border-2 ${-angBalance > 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
          <CardContent className="p-4">
            <p className="text-xs text-slate-600 font-medium">Mehedi Balance</p>
            <p className={`text-xl font-bold ${-angBalance > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
              {-angBalance > 0 ? '+' : ''}{formatCurrency(-angBalance, language)}
            </p>
            <p className="text-xs text-slate-500">{-angBalance > 0 ? 'Overpaid' : 'Underpaid'} (incl. settlements)</p>
          </CardContent>
        </Card>
      </div>

      {/* Settlement Banner + Settle Button */}
      {expenses.length > 0 && (
        <div className={`p-4 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-4 ${
          settlementNeeded ? 'bg-amber-50 border-2 border-amber-300' : 'bg-green-50 border-2 border-green-300'}`}>
          <div className="flex items-center gap-3 flex-1">
            {settlementNeeded ? (
              <ArrowRightLeft className="w-6 h-6 text-amber-600 flex-shrink-0" />
            ) : (
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
            )}
            <div>
              {settlementNeeded ? (
                <>
                  <p className="font-bold text-amber-800">Settlement Required</p>
                  <p className="text-sm text-amber-700">
                    <strong>{owes}</strong> owes <strong>{owedTo}</strong>{' '}
                    <span className="font-bold text-amber-900 text-lg">{formatCurrency(Math.abs(angBalance), language)}</span>{' '}
                    to settle all expenses fairly.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-bold text-green-800">All Settled!</p>
                  <p className="text-sm text-green-700">Both partners are squared up. No outstanding balances.</p>
                </>
              )}
            </div>
          </div>
          {settlementNeeded && (
            <Button
              onClick={() => {
                setShowSettleForm(!showSettleForm);
                setSettleData(d => ({ ...d, amount: Math.abs(angBalance).toFixed(2) }));
              }}
              className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
            >
              <Handshake className="w-4 h-4 mr-2" />
              Settle Now
            </Button>
          )}
        </div>
      )}

      {/* Settlement Form */}
      {showSettleForm && (
        <div className="p-5 bg-white rounded-xl border-2 border-dashed border-amber-400 shadow-sm space-y-4">
          <h3 className="text-lg font-bold flex items-center gap-2 text-amber-800">
            <Handshake className="w-5 h-5" /> Record Settlement
          </h3>
          <p className="text-sm text-slate-600">
            <strong>{owes}</strong> pays <strong>{owedTo}</strong> to settle the outstanding balance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Settlement Amount (BDT)</label>
              <Input
                type="number" step="0.01" min="0"
                value={settleData.amount}
                onChange={e => setSettleData(d => ({ ...d, amount: e.target.value }))}
                placeholder={Math.abs(angBalance).toFixed(2)}
              />
              <p className="text-xs text-slate-400 mt-1">Outstanding: {formatCurrency(Math.abs(angBalance))}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Date</label>
              <Input type="date" value={settleData.date} onChange={e => setSettleData(d => ({ ...d, date: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Notes (optional)</label>
              <Input value={settleData.notes} onChange={e => setSettleData(d => ({ ...d, notes: e.target.value }))} placeholder="e.g. bKash transfer" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSettle} disabled={settling || !(parseFloat(settleData.amount) > 0)} className="bg-amber-600 hover:bg-amber-700">
              {settling ? <Spinner size="sm" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              Confirm Settlement
            </Button>
            <Button variant="outline" onClick={() => setShowSettleForm(false)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); }} className="bg-emerald-600 hover:bg-emerald-700">
          <PlusCircle className="w-4 h-4 mr-1" /> Add Expense
        </Button>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-[200px]"><Tag className="w-3 h-3 mr-1" /><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {EXPENSE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterPaidBy} onValueChange={setFilterPaidBy}>
          <SelectTrigger className="w-[200px]"><Users className="w-3 h-3 mr-1" /><SelectValue placeholder="All Partners" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Partners</SelectItem>
            {PARTNERS.map(p => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
            <SelectItem value="both">Both (Shared Payment)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Form */}
      {showForm && <ExpenseForm onSave={handleCreate} onCancel={() => setShowForm(false)} />}

      {/* Expense List */}
      <div className="space-y-3">
        {/* Header Row */}
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider border-b">
          <button className="col-span-3 text-left flex items-center gap-1" onClick={() => toggleSort('title')}>
            Title <SortIcon field="title" />
          </button>
          <button className="col-span-2 text-left flex items-center gap-1" onClick={() => toggleSort('category')}>
            Category <SortIcon field="category" />
          </button>
          <button className="col-span-1 text-right flex items-center gap-1 justify-end" onClick={() => toggleSort('amount')}>
            Amount <SortIcon field="amount" />
          </button>
          <div className="col-span-1 text-center">Paid By</div>
          <div className="col-span-2 text-center">Split (A / M)</div>
          <button className="col-span-1 text-left flex items-center gap-1" onClick={() => toggleSort('date')}>
            Date <SortIcon field="date" />
          </button>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Receipt className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No expenses found</p>
            <p className="text-sm">Click "Add Expense" to get started</p>
          </div>
        )}

        {filtered.map(exp => {
          const ap = getActualPaid(exp);
          const paidByLabel = exp.paid_by === 'both'
            ? `A:${formatCurrency(ap.angshuman)} M:${formatCurrency(ap.mehedi)}`
            : (exp.paid_by === 'angshuman' ? 'Angsh.' : 'Mehedi');
          const paidByBadge = exp.paid_by === 'both'
            ? 'bg-amber-100 text-amber-700'
            : (exp.paid_by === 'angshuman' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700');

          return (
            <React.Fragment key={exp.id}>
              {editingId === exp.id ? (
                <ExpenseForm initialData={exp} onSave={handleUpdate} onCancel={() => setEditingId(null)} />
              ) : (
                <div
                  className="bg-white rounded-lg border hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => setExpandedId(expandedId === exp.id ? null : exp.id)}
                >
                  {/* Main Row */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-3 items-center">
                    <div className="col-span-12 md:col-span-3">
                      <p className="font-semibold text-slate-800 truncate">{exp.title}</p>
                      <p className="text-xs text-slate-400 md:hidden">{exp.date} · {exp.category}</p>
                    </div>
                    <div className="hidden md:block col-span-2">
                      <Badge className={`text-xs ${CATEGORY_COLORS[exp.category] || 'bg-gray-100'}`}>
                        {exp.category}
                      </Badge>
                    </div>
                    <div className="hidden md:block col-span-1 text-right font-bold text-slate-800">
                      {formatCurrency(exp.amount, language)}
                    </div>
                    <div className="hidden md:flex col-span-1 justify-center">
                      <Badge className={`text-xs ${paidByBadge}`}>
                        {exp.paid_by === 'both' ? 'Both' : paidByLabel}
                      </Badge>
                    </div>
                    <div className="hidden md:block col-span-2 text-center text-xs">
                      <span className="text-blue-600 font-medium">{formatCurrency(exp.split?.angshuman || 0)}</span>
                      <span className="text-slate-300 mx-1">/</span>
                      <span className="text-emerald-600 font-medium">{formatCurrency(exp.split?.mehedi || 0)}</span>
                    </div>
                    <div className="hidden md:flex col-span-1 items-center gap-1 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" /> {exp.date}
                    </div>
                    <div className="hidden md:flex col-span-2 justify-end gap-1" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(exp.id)}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(exp.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    {/* Mobile */}
                    <div className="col-span-12 md:hidden flex items-center justify-between">
                      <span className="font-bold">{formatCurrency(exp.amount, language)}</span>
                      <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Badge className={`text-xs ${paidByBadge}`}>
                          {exp.paid_by === 'both' ? 'Both' : paidByLabel}
                        </Badge>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(exp.id)}><Edit2 className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(exp.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedId === exp.id && (
                    <div className="px-4 pb-4 pt-1 border-t bg-slate-50 rounded-b-lg">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        {exp.description && (
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500">Description</p>
                            <p>{exp.description}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-slate-500">Who Paid</p>
                          {exp.paid_by === 'both' ? (
                            <div>
                              <p className="text-blue-700 font-medium">Angshuman: {formatCurrency(ap.angshuman)}</p>
                              <p className="text-emerald-700 font-medium">Mehedi: {formatCurrency(ap.mehedi)}</p>
                            </div>
                          ) : (
                            <p className="font-medium capitalize">{exp.paid_by} — {formatCurrency(exp.amount)}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Split Method</p>
                          <p className="capitalize">{exp.split_type}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Angshuman's Share (should bear)</p>
                          <p className="text-blue-700 font-medium">{formatCurrency(exp.split?.angshuman || 0)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Mehedi's Share (should bear)</p>
                          <p className="text-emerald-700 font-medium">{formatCurrency(exp.split?.mehedi || 0)}</p>
                        </div>
                        {exp.notes && (
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500">Notes</p>
                            <p className="text-slate-600 italic">{exp.notes}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-slate-500">Created</p>
                          <p className="text-xs">{new Date(exp.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Settlement History */}
      {settlements.length > 0 && (
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-purple-50 rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              Settlement History ({settlements.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {settlements.map(s => (
                <div key={s.id} className="px-5 py-3 flex items-center justify-between hover:bg-purple-50/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Handshake className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-slate-800">
                        {partnerName(s.paid_by)} paid {partnerName(s.paid_to)}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {s.date}</span>
                        {s.notes && <span className="text-slate-400">· {s.notes}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-purple-700">{formatCurrency(s.amount, language)}</span>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600" onClick={() => handleDeleteSettlement(s.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// EARNINGS TAB
// ─────────────────────────────────────────────────────────
function EarningsTab({ earnings, onRefresh, language }) {
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCat, setFilterCat] = useState('all');

  const filtered = useMemo(() => {
    let list = [...earnings];
    if (filterCat !== 'all') list = list.filter(e => e.category === filterCat);
    list.sort((a, b) => (a.date < b.date ? 1 : -1));
    return list;
  }, [earnings, filterCat]);

  const totalEarnings = filtered.reduce((s, e) => s + e.amount, 0);

  const handleCreate = async (data) => {
    await axios.post(`${API}/saas-earnings`, data);
    toast({ title: 'Earning added!' });
    setShowForm(false);
    onRefresh();
  };

  const handleUpdate = async (data) => {
    await axios.put(`${API}/saas-earnings/${editingId}`, data);
    toast({ title: 'Earning updated!' });
    setEditingId(null);
    onRefresh();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this earning?')) return;
    await axios.delete(`${API}/saas-earnings/${id}`);
    toast({ title: 'Earning deleted' });
    onRefresh();
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-600 font-medium">Total Earnings</p>
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalEarnings, language)}</p>
            <p className="text-xs text-emerald-500">{filtered.length} entries</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs text-blue-600 font-medium">Angshuman's Share (65%)</p>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(totalEarnings * 0.65, language)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
          <CardContent className="p-4">
            <p className="text-xs text-teal-600 font-medium">Mehedi's Share (35%)</p>
            <p className="text-xl font-bold text-teal-700">{formatCurrency(totalEarnings * 0.35, language)}</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => { setShowForm(!showForm); setEditingId(null); }} className="bg-blue-600 hover:bg-blue-700">
          <PlusCircle className="w-4 h-4 mr-1" /> Add Earning
        </Button>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-[200px]"><Tag className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {EARNING_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {showForm && <EarningForm onSave={handleCreate} onCancel={() => setShowForm(false)} />}

      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No earnings recorded</p>
            <p className="text-sm">Click "Add Earning" to start tracking revenue</p>
          </div>
        )}

        {filtered.map(earn => (
          <React.Fragment key={earn.id}>
            {editingId === earn.id ? (
              <EarningForm initialData={earn} onSave={handleUpdate} onCancel={() => setEditingId(null)} />
            ) : (
              <div className="bg-white rounded-lg border hover:border-slate-300 hover:shadow-sm transition-all p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="font-semibold text-slate-800">{earn.title}</h4>
                      <Badge className={`text-xs ${CATEGORY_COLORS[earn.category] || 'bg-gray-100'}`}>
                        {earn.category}
                      </Badge>
                    </div>
                    {earn.description && <p className="text-sm text-slate-600">{earn.description}</p>}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {earn.date}</span>
                      {earn.source && <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {earn.source}</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-blue-600">Angsh: {formatCurrency(earn.share?.angshuman || earn.amount * 0.65)}</span>
                      <span className="text-emerald-600">Mehedi: {formatCurrency(earn.share?.mehedi || earn.amount * 0.35)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-xl font-bold text-emerald-700">{formatCurrency(earn.amount, language)}</p>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(earn.id)}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(earn.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// SUMMARY / TOTALS TAB
// ─────────────────────────────────────────────────────────
function SummaryTab({ summary, settlements, loading, language }) {
  if (loading || !summary) {
    return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;
  }

  const s = summary;
  const ang = s.partners.angshuman;
  const meh = s.partners.mehedi;
  const isProfitable = s.net_profit >= 0;

  const angBal = ang.balance;
  const settlement = Math.abs(angBal) >= 1;
  const owes = angBal > 0 ? meh.name : ang.name;
  const owedTo = angBal > 0 ? ang.name : meh.name;

  return (
    <div className="space-y-6">
      {/* Top-level numbers */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-6">
            <TrendingDown className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-red-200 text-sm">Total Expenses</p>
            <p className="text-3xl font-bold">{formatCurrency(s.total_expenses, language)}</p>
            <p className="text-xs text-red-200 mt-1">{s.expense_count} entries</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-6">
            <TrendingUp className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-emerald-200 text-sm">Total Earnings</p>
            <p className="text-3xl font-bold">{formatCurrency(s.total_earnings, language)}</p>
            <p className="text-xs text-emerald-200 mt-1">{s.earning_count} entries</p>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br ${isProfitable ? 'from-blue-500 to-blue-600' : 'from-orange-500 to-orange-600'} text-white`}>
          <CardContent className="p-6">
            <Wallet className="w-8 h-8 mb-2 opacity-80" />
            <p className={`${isProfitable ? 'text-blue-200' : 'text-orange-200'} text-sm`}>
              {isProfitable ? 'Net Profit' : 'Net Loss'}
            </p>
            <p className="text-3xl font-bold">{formatCurrency(Math.abs(s.net_profit), language)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-6">
            <Handshake className="w-8 h-8 mb-2 opacity-80" />
            <p className="text-purple-200 text-sm">Total Settled</p>
            <p className="text-3xl font-bold">{formatCurrency(s.total_settled || 0, language)}</p>
            <p className="text-xs text-purple-200 mt-1">{s.settlement_count || 0} settlements</p>
          </CardContent>
        </Card>
      </div>

      {/* Partner Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[ang, meh].map((partner, i) => (
          <Card key={i} className={`border-2 ${i === 0 ? 'border-blue-200' : 'border-emerald-200'}`}>
            <CardHeader className={`pb-3 ${i === 0 ? 'bg-blue-50' : 'bg-emerald-50'} rounded-t-lg`}>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className={`w-5 h-5 ${i === 0 ? 'text-blue-600' : 'text-emerald-600'}`} />
                {partner.name}
                <Badge className={i === 0 ? 'bg-blue-500' : 'bg-emerald-500'}>{partner.share_pct}% stake</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Expense Share (should bear)</p>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(partner.expense_share, language)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Amount Paid in Expenses</p>
                  <p className="text-lg font-bold text-slate-800">{formatCurrency(partner.amount_paid, language)}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-600">Settlements Paid</p>
                  <p className="text-lg font-bold text-purple-700">{formatCurrency(partner.settlements_paid || 0, language)}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-600">Settlements Received</p>
                  <p className="text-lg font-bold text-purple-700">{formatCurrency(partner.settlements_received || 0, language)}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Earnings Share</p>
                  <p className="text-lg font-bold text-emerald-600">{formatCurrency(partner.earnings_share, language)}</p>
                </div>
                <div className={`p-3 rounded-lg ${partner.balance >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className="text-xs text-slate-500">Net Expense Balance</p>
                  <p className={`text-lg font-bold ${partner.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {partner.balance >= 0 ? '+' : ''}{formatCurrency(partner.balance, language)}
                  </p>
                  <p className="text-xs text-slate-400">{partner.balance >= 0 ? 'Overpaid' : 'Underpaid'} (incl. settlements)</p>
                </div>
              </div>

              <div className={`p-3 rounded-lg border-2 ${(partner.earnings_share - partner.expense_share) >= 0 ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                <p className="text-xs font-medium text-slate-600">Net Position (Earnings - Expenses)</p>
                <p className={`text-2xl font-bold ${(partner.earnings_share - partner.expense_share) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {(partner.earnings_share - partner.expense_share) >= 0 ? '+' : ''}
                  {formatCurrency(partner.earnings_share - partner.expense_share, language)}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Settlement Card */}
      {settlement ? (
        <Card className="border-2 border-amber-400 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <ArrowRightLeft className="w-7 h-7 text-amber-600" />
              <div>
                <h3 className="text-xl font-bold text-amber-800">Outstanding Balance</h3>
                <p className="text-amber-700">
                  <strong>{owes}</strong> should pay <strong>{owedTo}</strong>{' '}
                  <span className="text-2xl font-bold text-amber-900">{formatCurrency(Math.abs(angBal), language)}</span>{' '}
                  to balance all expenses (including past settlements).
                </p>
              </div>
            </div>
            <p className="text-xs text-amber-600">
              This considers expenses paid, responsibility shares (65/35), and all settlement transfers.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-green-400 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-7 h-7 text-green-600" />
              <div>
                <h3 className="text-xl font-bold text-green-800">All Settled!</h3>
                <p className="text-green-700">No outstanding balance between partners.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settlement History in Summary */}
      {settlements.length > 0 && (
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-purple-50 rounded-t-lg">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              Settlement History ({settlements.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {settlements.map(st => (
                <div key={st.id} className="px-5 py-3 flex items-center justify-between hover:bg-purple-50/50">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Handshake className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{partnerName(st.paid_by)} paid {partnerName(st.paid_to)}</p>
                      <p className="text-xs text-slate-500">{st.date}{st.notes ? ` · ${st.notes}` : ''}</p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-purple-700">{formatCurrency(st.amount, language)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-500" /> Expenses by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(s.expenses_by_category).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(s.expenses_by_category)
                  .sort(([, a], [, b]) => b - a)
                  .map(([cat, amount]) => (
                    <div key={cat} className="flex items-center justify-between py-1.5">
                      <Badge className={`text-xs ${CATEGORY_COLORS[cat] || 'bg-gray-100'}`}>{cat}</Badge>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-red-400 rounded-full" style={{ width: `${s.total_expenses ? (amount / s.total_expenses * 100) : 0}%` }} />
                        </div>
                        <span className="font-medium text-sm w-24 text-right">{formatCurrency(amount, language)}</span>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">No expenses yet</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> Earnings by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(s.earnings_by_category).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(s.earnings_by_category)
                  .sort(([, a], [, b]) => b - a)
                  .map(([cat, amount]) => (
                    <div key={cat} className="flex items-center justify-between py-1.5">
                      <Badge className={`text-xs ${CATEGORY_COLORS[cat] || 'bg-gray-100'}`}>{cat}</Badge>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${s.total_earnings ? (amount / s.total_earnings * 100) : 0}%` }} />
                        </div>
                        <span className="font-medium text-sm w-24 text-right">{formatCurrency(amount, language)}</span>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">No earnings yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      {Object.keys(s.monthly_trend).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" /> Monthly Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-slate-500">
                    <th className="text-left py-2 px-3">Month</th>
                    <th className="text-right py-2 px-3">Expenses</th>
                    <th className="text-right py-2 px-3">Earnings</th>
                    <th className="text-right py-2 px-3">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(s.monthly_trend).reverse().map(([month, data]) => {
                    const net = data.earnings - data.expenses;
                    return (
                      <tr key={month} className="border-b last:border-0 hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium">{month}</td>
                        <td className="py-2 px-3 text-right text-red-600">{formatCurrency(data.expenses, language)}</td>
                        <td className="py-2 px-3 text-right text-emerald-600">{formatCurrency(data.earnings, language)}</td>
                        <td className={`py-2 px-3 text-right font-bold ${net >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                          {net >= 0 ? '+' : ''}{formatCurrency(net, language)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────
export default function SaasFinanceModule({ language }) {
  const [expenses, setExpenses] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('expenses');

  const fetchAll = async () => {
    try {
      const [expRes, earnRes, settleRes, sumRes] = await Promise.all([
        axios.get(`${API}/saas-expenses`),
        axios.get(`${API}/saas-earnings`),
        axios.get(`${API}/saas-settlements`),
        axios.get(`${API}/saas-finance-summary`),
      ]);
      setExpenses(expRes.data);
      setEarnings(earnRes.data);
      setSettlements(settleRes.data);
      setSummary(sumRes.data);
    } catch (err) {
      console.error('Failed to fetch finance data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  if (loading) {
    return <div className="flex justify-center py-16"><Spinner size="lg" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-lg flex items-center justify-center">
          <DollarSign className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">SaaS Finance Tracker</h2>
          <p className="text-xs text-slate-500">
            Angshuman Biswas (65%) &amp; Mehedi Hassan (35%) — Expense, Earnings &amp; Settlement Management
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="expenses" className="gap-1">
            <Receipt className="w-3.5 h-3.5" /> Expenses ({expenses.length})
          </TabsTrigger>
          <TabsTrigger value="earnings" className="gap-1">
            <TrendingUp className="w-3.5 h-3.5" /> Earnings ({earnings.length})
          </TabsTrigger>
          <TabsTrigger value="summary" className="gap-1">
            <PieChart className="w-3.5 h-3.5" /> Summary
          </TabsTrigger>
        </TabsList>

        <TabsContent value="expenses">
          <ExpensesTab expenses={expenses} settlements={settlements} onRefresh={fetchAll} language={language} />
        </TabsContent>

        <TabsContent value="earnings">
          <EarningsTab earnings={earnings} onRefresh={fetchAll} language={language} />
        </TabsContent>

        <TabsContent value="summary">
          <SummaryTab summary={summary} settlements={settlements} loading={loading} language={language} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
